import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Calendar, Clock, Users, Edit } from 'lucide-react';
import { format } from 'date-fns';

/**
 * Dialog shown when updating a recurring meeting.
 * Lets the user choose the scope of the update:
 *   - 'this'           → Only this occurrence
 *   - 'this_and_following' → This and all following occurrences
 *   - 'all'            → All events in the series
 */
const UpdateRecurringMeetingDialog = ({
    open,
    onClose,
    meeting,
    onConfirm,
    isLoading = false,
    allowFollowingScope = false, // Only show 'following' option when editing from Calendar
}) => {
    const [updateScope, setUpdateScope] = useState('this');

    // Reset selection whenever the dialog opens
    useEffect(() => {
        if (open) {
            setUpdateScope('this');
        }
    }, [open]);

    if (!meeting) return null;

    const formatTime = (time) => {
        if (!time) return 'N/A';
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const getRecurrenceLabel = () => {
        if (meeting.recurrence_label) return meeting.recurrence_label;
        switch (meeting.recurrence) {
            case 'daily': return 'Daily';
            case 'weekly': return 'Weekly';
            case 'monthly': return 'Monthly';
            case 'yearly': return 'Yearly';
            case 'custom': return 'Custom';
            default: return meeting.recurrence || '';
        }
    };

    const handleConfirm = () => {
        onConfirm(updateScope);
    };

    const allOptions = [
        {
            value: 'this',
            label: 'This event',
            description: `Only the occurrence on ${meeting.scheduled_date
                    ? format(new Date(meeting.scheduled_date), 'EEEE, MMMM d, yyyy')
                    : 'this date'
                } will be updated. All other occurrences remain unchanged.`,
        },
        {
            value: 'following',
            label: 'This and following events',
            description: `This occurrence and all future occurrences will be updated. Past occurrences remain unchanged.`,
        },
        {
            value: 'all',
            label: 'All events',
            description: `Every occurrence in the series will be updated, including past and future meetings.`,
        },
    ];

    // Show 'following' only when opened from Calendar (allowFollowingScope=true)
    const options = allOptions.filter(
        (opt) => opt.value !== 'following' || allowFollowingScope
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[480px] min-h-[60vh] w-[95vw] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Edit className="h-5 w-5 text-blue-500" />
                        Edit Recurring Meeting
                    </DialogTitle>
                    <DialogDescription>
                        This is a recurring meeting. Which occurrences would you like to update?
                    </DialogDescription>
                </DialogHeader>


                {/* Scope selection */}
                <RadioGroup
                    value={updateScope}
                    onValueChange={setUpdateScope}
                    className="space-y-3"
                >
                    {options.map((option) => (
                        <div
                            key={option.value}
                            className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${updateScope === option.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'hover:bg-gray-50'
                                }`}
                            onClick={() => setUpdateScope(option.value)}
                        >
                            <RadioGroupItem
                                value={option.value}
                                id={`update-${option.value}`}
                                className="mt-1 h-4 w-4 shrink-0"
                            />
                            <div className="flex-1">
                                <Label
                                    htmlFor={`update-${option.value}`}
                                    className="font-medium cursor-pointer"
                                >
                                    {option.label}
                                </Label>
                                <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                            </div>
                        </div>
                    ))}
                </RadioGroup>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={isLoading}>
                        {isLoading ? 'Updating...' : 'Update Meeting'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateRecurringMeetingDialog;
